import React, { useEffect, useState } from 'react';
import { usePos } from '../context/PosContext';
import { LoyaltySettings } from '../types';
import { getLoyaltySettings, saveLoyaltySettings } from '../services/loyalty';
import {
  backupToCloud,
  restoreFromCloud,
  getLatestBackupMeta,
  getBackupKey,
  setBackupKey,
} from '../services/cloudBackup';
import {
  isNativeApp,
  getBackupFolder,
  chooseBackupFolder,
  backupToFolder,
  restoreFromFolder,
  getAutoBackupSettings,
  setAutoBackupSettings,
  getLastAutoBackup,
  AutoBackupSettings,
} from '../services/folderBackup';
import { getBackupEmail, setBackupEmail, emailBackup } from '../services/emailBackup';
import {
  getSyncStatus,
  getSyncToken,
  setSyncConfig,
  SyncStatus,
  SYNC_STATUS_EVENT,
} from '../services/sync';
import { idbAllKeys, idbGetRaw, idbSetRaw, idbDel } from '../services/idb';
import { loadDemoData as dbLoadDemoData } from '../services/db';
import {
  cutoffForMonths,
  previewArchive,
  buildArchiveFile,
  commitArchive,
  importArchivedSales,
  ArchivePreview,
} from '../services/salesArchive';
import {
  validate,
  activate,
  deactivate,
  cachedStatus,
  getDeviceId,
  getDeviceName,
  setDeviceName,
  LicenseStatus,
} from '../services/license';
import './Settings.css';

const CURRENCIES = ['₹', '$', '€', '£', '₨', 'AED', 'RM'];

const Settings: React.FC = () => {
  const { config, updateConfig, isOn } = usePos();
  const [draft, setDraft] = useState({ ...config });

  // Loyalty scheme (mirrors the Firebase LoyaltyPoint bean). Gated at runtime by
  // the App Setting "E - Loyalty Points" toggle as well as its own Enabled flag.
  const [loyalty, setLoyalty] = useState<LoyaltySettings>(() => getLoyaltySettings());
  const setLoy = (k: keyof LoyaltySettings, v: number | boolean) =>
    setLoyalty((prev) => ({ ...prev, [k]: v }));
  const saveLoyalty = () => {
    saveLoyaltySettings({
      ...loyalty,
      claimPercentage: Number(loyalty.claimPercentage) || 0,
      pointConversion: Number(loyalty.pointConversion) || 0,
      minPoint: Number(loyalty.minPoint) || 0,
      maxPoint: Number(loyalty.maxPoint) || 0,
    });
    alert('Loyalty scheme saved.');
  };

  // Cloud backup/restore (Supabase). The "backup key" identifies this store's
  // snapshots; any terminal using the same key can restore them.
  const [cloudKey, setCloudKey] = useState<string>(() => getBackupKey());
  const [cloudBusy, setCloudBusy] = useState<'' | 'backup' | 'restore'>('');
  const [lastBackup, setLastBackup] = useState<string>('');

  const refreshLastBackup = React.useCallback(async (key: string) => {
    if (!key.trim()) {
      setLastBackup('');
      return;
    }
    try {
      const meta = await getLatestBackupMeta(key);
      setLastBackup(meta ? new Date(meta.created_at).toLocaleString() : 'No backup yet');
    } catch (err) {
      setLastBackup((err as Error).message);
    }
  }, []);

  useEffect(() => {
    refreshLastBackup(getBackupKey());
  }, [refreshLastBackup]);

  const onCloudKeyChange = (v: string) => {
    setCloudKey(v);
    setBackupKey(v);
  };

  // ---- Server sync (LAN real-time multi-terminal) ----
  const [sync, setSync] = useState<SyncStatus>(() => getSyncStatus());
  const [syncUrl, setSyncUrl] = useState<string>(() => getSyncStatus().url);
  const [syncToken, setSyncToken] = useState<string>(() => getSyncToken());
  const [syncBusy, setSyncBusy] = useState(false);

  useEffect(() => {
    const onStatus = (e: Event) => {
      const s = (e as CustomEvent<SyncStatus>).detail;
      setSync(s);
      // Keep the URL field in step unless the user is mid-edit (field focused).
      if (document.activeElement?.getAttribute('data-sync-url') == null) setSyncUrl(s.url);
    };
    window.addEventListener(SYNC_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(SYNC_STATUS_EVENT, onStatus);
  }, []);

  const applySync = async (enabled: boolean) => {
    setSyncBusy(true);
    try {
      await setSyncConfig(enabled, syncUrl, syncToken);
    } finally {
      setSyncBusy(false);
    }
  };

  const doCloudBackup = async () => {
    if (!cloudKey.trim()) {
      alert('Enter a backup key first (e.g. your store name or a passphrase).');
      return;
    }
    setCloudBusy('backup');
    try {
      const meta = await backupToCloud(cloudKey);
      setLastBackup(new Date(meta.created_at).toLocaleString());
      alert('Backup uploaded to cloud.');
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCloudBusy('');
    }
  };

  const doCloudRestore = async () => {
    if (!cloudKey.trim()) {
      alert('Enter the backup key to restore from.');
      return;
    }
    if (
      !window.confirm(
        'Restoring overwrites ALL local data with the latest cloud backup for this key. Continue?'
      )
    )
      return;
    setCloudBusy('restore');
    try {
      await restoreFromCloud(cloudKey);
      alert('Data restored from cloud. Reloading…');
      window.location.reload();
    } catch (err) {
      alert((err as Error).message);
      setCloudBusy('');
    }
  };

  // Email backup — send the full dataset as a JSON attachment via the device's
  // share sheet (or mail client fallback). The recipient address is remembered.
  const [backupEmail, setBackupEmailState] = useState<string>(() => getBackupEmail());
  const [emailBusy, setEmailBusy] = useState(false);

  const onBackupEmailChange = (v: string) => {
    setBackupEmailState(v);
    setBackupEmail(v);
  };

  const doEmailBackup = async () => {
    setEmailBusy(true);
    try {
      const { method } = await emailBackup(backupEmail);
      if (method === 'mailto') {
        alert('Backup downloaded. Your email app is opening — attach the downloaded file to send it.');
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') alert((err as Error).message);
    } finally {
      setEmailBusy(false);
    }
  };

  // Folder backup/restore (Android only) — write/read the full dataset to a
  // user-chosen device folder via the native Storage Access Framework picker.
  const native = isNativeApp();
  const [folderName, setFolderName] = useState<string>('');
  const [folderBusy, setFolderBusy] = useState<'' | 'pick' | 'backup' | 'restore'>('');

  // Automatic folder backup — enable/frequency, persisted in localStorage.
  const [auto, setAuto] = useState<AutoBackupSettings>(() => getAutoBackupSettings());
  const lastAuto = getLastAutoBackup();
  const updateAuto = (next: AutoBackupSettings) => {
    setAuto(next);
    setAutoBackupSettings(next);
  };

  useEffect(() => {
    if (!native) return;
    getBackupFolder().then(setFolderName).catch(() => {});
  }, [native]);

  const doChooseFolder = async () => {
    setFolderBusy('pick');
    try {
      setFolderName(await chooseBackupFolder());
    } catch (err) {
      if ((err as { code?: string }).code !== 'CANCELLED') alert((err as Error).message);
    } finally {
      setFolderBusy('');
    }
  };

  const doFolderBackup = async () => {
    if (!folderName) {
      alert('Choose a backup folder first.');
      return;
    }
    setFolderBusy('backup');
    try {
      const name = await backupToFolder();
      alert(`Backup saved to your folder as "${name}".`);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setFolderBusy('');
    }
  };

  const doFolderRestore = async () => {
    if (
      !window.confirm(
        'Restoring overwrites ALL local data with the selected backup file. Continue?'
      )
    )
      return;
    setFolderBusy('restore');
    try {
      await restoreFromFolder();
      alert('Data restored. Reloading…');
      window.location.reload();
    } catch (err) {
      if ((err as { code?: string }).code !== 'CANCELLED') alert((err as Error).message);
      setFolderBusy('');
    }
  };

  // Licence status (read from cache immediately, then refresh online).
  const [license, setLicense] = useState<LicenseStatus>(() => cachedStatus());
  const [deviceLabel, setDeviceLabel] = useState<string>(() => getDeviceName());
  useEffect(() => {
    validate().then(setLicense).catch(() => {});
  }, []);

  const [activateKey, setActivateKey] = useState('');
  const [activating, setActivating] = useState(false);
  const doActivate = async () => {
    if (!activateKey.trim()) {
      alert('Enter a licence key.');
      return;
    }
    setActivating(true);
    try {
      await activate(activateKey);
      alert('Licence activated. Reloading…');
      window.location.reload();
    } catch (e) {
      alert((e as Error).message);
      setActivating(false);
    }
  };

  const doDeactivate = async () => {
    if (
      !window.confirm(
        'Deactivate this device? It will free a licence slot and lock the app until a key is re-entered.'
      )
    )
      return;
    await deactivate();
    alert('Device deactivated. Reloading…');
    window.location.reload();
  };

  // Archive & clear old bills — move sales older than a cutoff into a downloaded
  // JSON file, then remove them from the live array to keep billing/reports fast
  // and well clear of the localStorage quota. The file can be re-imported later.
  const [archiveMonths, setArchiveMonths] = useState<string>('12');
  const [archiveDate, setArchiveDate] = useState<string>('');
  const [archiveBusy, setArchiveBusy] = useState(false);

  // The chosen cutoff: a fixed calendar date in "custom" mode, else N months ago.
  const archiveCutoff = (): Date | null => {
    if (archiveMonths === 'custom') {
      if (!archiveDate) return null;
      const d = new Date(`${archiveDate}T00:00:00`);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    return cutoffForMonths(Number(archiveMonths));
  };

  // Live preview of what the current cutoff would archive vs. keep.
  const [archivePreview, setArchivePreview] = useState<ArchivePreview | null>(null);
  useEffect(() => {
    const cutoff =
      archiveMonths === 'custom'
        ? archiveDate
          ? new Date(`${archiveDate}T00:00:00`)
          : null
        : cutoffForMonths(Number(archiveMonths));
    if (cutoff && !Number.isNaN(cutoff.getTime())) {
      previewArchive(cutoff).then(setArchivePreview);
    } else {
      setArchivePreview(null);
    }
  }, [archiveMonths, archiveDate]);

  const doArchive = async () => {
    const cutoff = archiveCutoff();
    if (!cutoff) {
      alert('Choose a valid cutoff date first.');
      return;
    }
    const file = await buildArchiveFile(cutoff);
    if (!file) {
      alert('No bills are older than the chosen cutoff — nothing to archive.');
      return;
    }
    if (
      !window.confirm(
        `${file.count} bill(s) will be saved to a backup file and then removed from this device. ` +
          `They will no longer appear in on-device reports (re-import the file to bring them back). Continue?`
      )
    )
      return;
    setArchiveBusy(true);
    try {
      // Save the archive file FIRST; only clear once it has been written.
      const blob = new Blob([file.content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename;
      a.click();
      URL.revokeObjectURL(url);

      const removed = await commitArchive(cutoff);
      setArchivePreview(await previewArchive(cutoff));
      alert(`Archived and cleared ${removed} bill(s) to "${file.filename}". Keep this file safe.`);
    } catch (err) {
      alert(`Archive failed, no bills were removed: ${(err as Error).message}`);
    } finally {
      setArchiveBusy(false);
    }
  };

  const importArchive = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { added, skipped } = await importArchivedSales(reader.result as string);
        const cutoff = archiveCutoff();
        if (cutoff && !Number.isNaN(cutoff.getTime())) setArchivePreview(await previewArchive(cutoff));
        alert(
          added > 0
            ? `Imported ${added} bill(s) back into reports.${skipped ? ` ${skipped} already present.` : ''}`
            : 'No new bills to import — all were already present.'
        );
      } catch {
        alert('Invalid sales archive file.');
      } finally {
        input.value = '';
      }
    };
    reader.readAsText(file);
  };

  const set = (k: keyof typeof draft, v: string | number) =>
    setDraft((prev) => ({ ...prev, [k]: v }));

  const save = () => {
    updateConfig({
      ...draft,
      taxRatePercent: Number(draft.taxRatePercent) || 0,
    });
    alert('Settings saved.');
  };

  const reset = () => {
    if (window.confirm('Reset settings to defaults?')) {
      const defaults = {
        storeName: 'EPOS',
        currencySymbol: '₹',
        taxRatePercent: 5,
        address: '',
        phone: '',
        gstin: '',
        footerNote: '',
      };
      setDraft(defaults);
      updateConfig(defaults);
    }
  };

  // Export every POS collection from the database as a single JSON backup.
  const exportData = async () => {
    const dump: Record<string, unknown> = {};
    for (const key of await idbAllKeys()) {
      if (!key.startsWith('sconnect.')) continue;
      const raw = await idbGetRaw(key);
      if (raw == null) continue;
      try {
        dump[key] = JSON.parse(raw);
      } catch {
        /* ignore */
      }
    }
    const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sconnect-backup-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importData = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const data = JSON.parse(reader.result as string) as Record<string, unknown>;
        for (const [k, v] of Object.entries(data)) {
          if (k.startsWith('sconnect.')) await idbSetRaw(k, JSON.stringify(v));
        }
        alert('Data imported. Reloading…');
        window.location.reload();
      } catch {
        alert('Invalid backup file.');
      }
    };
    reader.readAsText(file);
  };

  const clearData = async () => {
    if (window.confirm('This clears ALL products, sales and data. Continue?')) {
      for (const k of await idbAllKeys()) {
        if (k.startsWith('sconnect.')) await idbDel(k);
      }
      alert('Data cleared. Reloading…');
      window.location.reload();
    }
  };

  const loadDemoData = async () => {
    if (
      window.confirm(
        'This loads the sample product catalog and table layout, overwriting any existing products and tables. Continue?'
      )
    ) {
      await dbLoadDemoData();
      alert('Demo data loaded. Reloading…');
      window.location.reload();
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
        <div className="header-actions">
          <button className="reset-btn" onClick={reset}>
            Reset to Defaults
          </button>
          <button className="save-btn" onClick={save}>
            Save Settings
          </button>
        </div>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>Store Information</h2>
          <div className="setting-group">
            <label>
              Store Name
              <input value={draft.storeName} onChange={(e) => set('storeName', e.target.value)} />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Address
              <input value={draft.address ?? ''} onChange={(e) => set('address', e.target.value)} />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Phone
              <input value={draft.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
            </label>
            <label>
              GSTIN
              <input value={draft.gstin ?? ''} onChange={(e) => set('gstin', e.target.value)} />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Currency Symbol
              <select value={draft.currencySymbol} onChange={(e) => set('currencySymbol', e.target.value)}>
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Default Tax Rate (%)
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={draft.taxRatePercent}
                onChange={(e) => set('taxRatePercent', parseFloat(e.target.value) || 0)}
              />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Receipt Footer Note
              <input
                value={draft.footerNote ?? ''}
                onChange={(e) => set('footerNote', e.target.value)}
                placeholder="Thank you for your purchase!"
              />
            </label>
          </div>
          <div className="setting-group">
            <label>
              UPI ID (VPA)
              <input
                value={draft.upiId ?? ''}
                onChange={(e) => set('upiId', e.target.value)}
                placeholder="store@upi"
              />
            </label>
            <label>
              UPI Payee Name
              <input
                value={draft.upiPayeeName ?? ''}
                onChange={(e) => set('upiPayeeName', e.target.value)}
                placeholder={draft.storeName}
              />
            </label>
          </div>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            With a UPI ID set, a scan-to-pay QR shows on the payment dialog for digital
            tenders, and prints on receipts per App Setting → UPI → "UPI QRCODE In Bill".
          </p>
        </div>

        <div className="settings-section">
          <h2>Loyalty Points</h2>
          {!isOn('enable_loyalty_points') && (
            <p style={{ color: 'var(--color-muted)' }}>
              Loyalty points are currently turned off in App Setting → Customer → “E - Loyalty
              Points”. Configure the scheme here; it applies once that toggle is on.
            </p>
          )}
          <div className="setting-group">
            <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={loyalty.enabled}
                onChange={(e) => setLoy('enabled', e.target.checked)}
                style={{ width: 'auto' }}
              />
              Enable loyalty scheme
            </label>
          </div>
          <div className="setting-group">
            <label>
              Spend per point earned ({config.currencySymbol})
              <input
                type="number"
                min="1"
                value={loyalty.claimPercentage}
                onChange={(e) => setLoy('claimPercentage', parseFloat(e.target.value) || 0)}
              />
            </label>
            <label>
              Points per {config.currencySymbol}1 redeemed
              <input
                type="number"
                min="1"
                value={loyalty.pointConversion}
                onChange={(e) => setLoy('pointConversion', parseFloat(e.target.value) || 0)}
              />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Minimum points to redeem
              <input
                type="number"
                min="0"
                value={loyalty.minPoint}
                onChange={(e) => setLoy('minPoint', parseFloat(e.target.value) || 0)}
              />
            </label>
            <label>
              Maximum points per bill
              <input
                type="number"
                min="0"
                value={loyalty.maxPoint}
                onChange={(e) => setLoy('maxPoint', parseFloat(e.target.value) || 0)}
              />
            </label>
          </div>
          <div className="data-actions">
            <button className="save-btn" onClick={saveLoyalty}>
              Save Loyalty Scheme
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h2>Printing</h2>
          <p style={{ color: 'var(--color-muted)' }}>
            The web app prints receipts through the browser print dialog (works with any
            system-installed thermal or A4 printer). Native Bluetooth/USB driver pairing from the
            Android app is not available in the browser.
          </p>
        </div>

        <div className="settings-section">
          <h2>Licence</h2>
          <div className="license-status">
            <div className="license-row">
              <span>Status</span>
              <strong className={`license-badge license-${license.phase}`}>
                {license.phase === 'active' && 'Active'}
                {license.phase === 'trial' && 'Trial'}
                {license.phase === 'grace' && 'Active (offline)'}
                {license.phase === 'expired' && 'Expired'}
                {license.phase === 'revoked' && 'Revoked'}
                {license.phase === 'unactivated' && 'Not activated'}
              </strong>
            </div>
            {license.plan && (
              <div className="license-row">
                <span>Plan</span>
                <strong>{license.plan}</strong>
              </div>
            )}
            {license.key && (
              <div className="license-row">
                <span>Key</span>
                <strong>{license.key}</strong>
              </div>
            )}
            {license.expiresAt && (
              <div className="license-row">
                <span>Expires</span>
                <strong>
                  {new Date(license.expiresAt).toLocaleDateString()}
                  {license.daysLeft != null && ` (${license.daysLeft} days)`}
                </strong>
              </div>
            )}
            {license.expiresAt === null && license.phase !== 'trial' && (
              <div className="license-row">
                <span>Expires</span>
                <strong>Never (lifetime)</strong>
              </div>
            )}
            <div className="license-row">
              <span>Device ID</span>
              <strong style={{ fontSize: 12, fontFamily: 'monospace' }}>{getDeviceId()}</strong>
            </div>
          </div>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>{license.message}</p>
          <div className="setting-group">
            <label>
              Device Name (shown in your licence dashboard)
              <input
                value={deviceLabel}
                onChange={(e) => setDeviceLabel(e.target.value)}
                onBlur={() => setDeviceName(deviceLabel)}
                placeholder="e.g. Front counter"
              />
            </label>
          </div>
          {license.key ? (
            <div className="data-actions">
              <button className="clear-data-btn" onClick={doDeactivate}>
                Deactivate This Device
              </button>
            </div>
          ) : (
            <>
              <div className="setting-group">
                <label>
                  Licence Key
                  <input
                    value={activateKey}
                    onChange={(e) => setActivateKey(e.target.value.toUpperCase())}
                    placeholder="EPOS-XXXX-XXXX-XXXX"
                    spellCheck={false}
                    style={{ fontFamily: 'monospace', letterSpacing: 1 }}
                  />
                </label>
              </div>
              <div className="data-actions">
                <button className="save-btn" onClick={doActivate} disabled={activating}>
                  {activating ? 'Activating…' : 'Activate Licence'}
                </button>
              </div>
            </>
          )}
        </div>

        {native && (
          <div className="settings-section">
            <h2>Folder Backup &amp; Restore</h2>
            <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
              Save a full copy of all POS data (products, sales, customers, settings…) as a JSON
              file in a folder on this device. Pick any folder — internal storage, an SD card, or a
              synced Drive/Dropbox folder. To restore, choose a previously saved backup file.
            </p>
            <div className="setting-group">
              <label>
                Backup Folder
                <input
                  value={folderName || 'No folder chosen'}
                  readOnly
                  style={{ color: folderName ? undefined : 'var(--color-muted)' }}
                />
              </label>
            </div>
            <div className="data-actions">
              <button className="save-btn" onClick={doChooseFolder} disabled={folderBusy !== ''}>
                {folderBusy === 'pick' ? 'Opening…' : 'Choose Folder'}
              </button>
              <button
                className="backup-btn"
                onClick={doFolderBackup}
                disabled={folderBusy !== '' || !folderName}
              >
                {folderBusy === 'backup' ? 'Backing up…' : 'Backup to Folder'}
              </button>
              <button className="restore-btn" onClick={doFolderRestore} disabled={folderBusy !== ''}>
                {folderBusy === 'restore' ? 'Restoring…' : 'Restore from File'}
              </button>
            </div>

            <h3 style={{ marginTop: 24 }}>Automatic Backup</h3>
            <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
              Once a backup folder is chosen, the app can write a fresh backup to it automatically
              — on startup and in the background — so you don't have to remember. Only the 5 most
              recent backups are kept; older ones are deleted automatically. Choose a folder above
              first.
            </p>
            <div className="setting-group">
              <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={auto.enabled}
                  disabled={!folderName}
                  onChange={(e) => updateAuto({ ...auto, enabled: e.target.checked })}
                  style={{ width: 'auto' }}
                />
                Automatically back up to this folder
              </label>
            </div>
            {auto.enabled && (
              <>
                <div className="setting-group">
                  <label>
                    Frequency
                    <select
                      value={auto.intervalHours}
                      onChange={(e) => updateAuto({ ...auto, intervalHours: Number(e.target.value) })}
                    >
                      <option value={6}>Every 6 hours</option>
                      <option value={12}>Every 12 hours</option>
                      <option value={24}>Daily</option>
                      <option value={168}>Weekly</option>
                    </select>
                  </label>
                </div>
                <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
                  {lastAuto ? (
                    <>
                      Last automatic backup: <strong>{new Date(lastAuto).toLocaleString()}</strong>
                    </>
                  ) : (
                    'No automatic backup yet — the first one runs when due.'
                  )}
                </p>
              </>
            )}
          </div>
        )}

        <div className="settings-section">
          <h2>Server Sync (Multi-Terminal)</h2>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            Keep several terminals (billing counters, captain tablets, kitchen) in sync in real
            time over your shop's Wi-Fi/LAN, while every terminal keeps working offline. One
            Windows PC runs the sync server — from that PC's project folder run{' '}
            <code>npm run server</code>, then point each terminal at its address below (on the
            server PC itself, use <code>http://localhost:4560</code>).
          </p>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            <strong>Set up your main data device first</strong> — the first terminal to connect
            seeds the server with its data; the others then adopt that shared dataset.
          </p>
          <div className="setting-group">
            <label>
              Server Address
              <input
                data-sync-url=""
                value={syncUrl}
                onChange={(e) => setSyncUrl(e.target.value)}
                placeholder="http://192.168.1.50:4560"
                spellCheck={false}
                disabled={sync.enabled}
              />
            </label>
          </div>
          <div className="setting-group">
            <label>
              Access Token <span style={{ color: 'var(--color-muted)', fontWeight: 400 }}>(optional)</span>
              <input
                value={syncToken}
                onChange={(e) => setSyncToken(e.target.value)}
                placeholder="leave blank if the server has no token"
                spellCheck={false}
                autoComplete="off"
                disabled={sync.enabled}
              />
            </label>
            <p style={{ color: 'var(--color-muted)', fontSize: 12, marginTop: 4 }}>
              Set this to match the server's <code>SYNC_TOKEN</code> so only your terminals can
              connect. Must be identical on every terminal.
            </p>
          </div>
          <p style={{ fontSize: 13 }}>
            Status:{' '}
            {!sync.enabled ? (
              <strong style={{ color: 'var(--color-muted)' }}>Off (single device)</strong>
            ) : sync.connected ? (
              <strong style={{ color: '#1a9e57' }}>● Connected</strong>
            ) : (
              <strong style={{ color: '#d08a1a' }}>● Offline — retrying…</strong>
            )}
            {sync.enabled && sync.pending > 0 && (
              <span style={{ color: 'var(--color-muted)' }}> · {sync.pending} change(s) queued</span>
            )}
          </p>
          <div className="data-actions">
            {!sync.enabled ? (
              <button className="backup-btn" onClick={() => applySync(true)} disabled={syncBusy}>
                {syncBusy ? 'Connecting…' : 'Turn On Sync'}
              </button>
            ) : (
              <button className="restore-btn" onClick={() => applySync(false)} disabled={syncBusy}>
                {syncBusy ? 'Stopping…' : 'Turn Off Sync'}
              </button>
            )}
          </div>

          <details style={{ marginTop: 14, fontSize: 13 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Setup help &amp; troubleshooting
            </summary>
            <div style={{ color: 'var(--color-muted)', marginTop: 8, lineHeight: 1.6 }}>
              <strong>Quick setup</strong>
              <ol style={{ margin: '4px 0 10px', paddingLeft: 20 }}>
                <li>
                  On the shop's main Windows PC, run the server: from the project folder run{' '}
                  <code>npm run server</code> (or double-click{' '}
                  <code>server\start-server.bat</code>). Allow it through the firewall the first
                  time.
                </li>
                <li>
                  Find that PC's address with <code>ipconfig</code> (the IPv4 line, e.g.{' '}
                  <code>192.168.1.50</code>).
                </li>
                <li>
                  <strong>On your main data device first</strong>, set the Server Address to{' '}
                  <code>http://&lt;that-ip&gt;:4560</code> and Turn On Sync — it uploads your data
                  and becomes the shared copy.
                </li>
                <li>
                  On every other terminal, enter the same address (and token, if any) and Turn On
                  Sync — they adopt the shared data.
                </li>
              </ol>
              <strong>Status meaning</strong>
              <ul style={{ margin: '4px 0 10px', paddingLeft: 20 }}>
                <li>
                  <span style={{ color: '#1a9e57' }}>● Connected</span> — live and syncing.
                </li>
                <li>
                  <span style={{ color: '#d08a1a' }}>● Offline — retrying…</span> — can't reach the
                  server; this terminal still works and queues changes, then catches up
                  automatically.
                </li>
              </ul>
              <strong>If it won't connect,</strong> open{' '}
              <code>http://&lt;server-ip&gt;:4560/health</code> in this device's browser — a small
              status appears when the server is reachable. Check the address, that the server is
              running, the firewall, and that the token matches.
              <p style={{ marginTop: 8 }}>
                Full guide (deployment, restaurant, multi-branch head office, and a test
                checklist): <code>docs/MULTI-TERMINAL-SETUP.md</code> in the project folder.
              </p>
            </div>
          </details>
        </div>

        <div className="settings-section">
          <h2>Cloud Backup &amp; Restore</h2>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            Back up all POS data (products, sales, customers, settings…) to the cloud and
            restore it on any device. Enter a <strong>backup key</strong> that identifies this
            store — keep it private; anyone with the same key can access these backups.
          </p>
          <div className="setting-group">
            <label>
              Backup Key
              <input
                value={cloudKey}
                onChange={(e) => onCloudKeyChange(e.target.value)}
                onBlur={(e) => refreshLastBackup(e.target.value)}
                placeholder="e.g. my-store-2026"
              />
            </label>
          </div>
          {lastBackup && (
            <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
              Latest cloud backup: <strong>{lastBackup}</strong>
            </p>
          )}
          <div className="data-actions">
            <button className="backup-btn" onClick={doCloudBackup} disabled={cloudBusy !== ''}>
              {cloudBusy === 'backup' ? 'Backing up…' : 'Backup to Cloud'}
            </button>
            <button className="restore-btn" onClick={doCloudRestore} disabled={cloudBusy !== ''}>
              {cloudBusy === 'restore' ? 'Restoring…' : 'Restore from Cloud'}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h2>Email Backup</h2>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            Send a full copy of all POS data (products, sales, customers, settings…) as a JSON
            attachment by email. On phones and the Android app this opens the share sheet so you
            can pick Gmail/Outlook with the file already attached; in a desktop browser the file
            is downloaded and your mail app opens for you to attach it.
          </p>
          <div className="setting-group">
            <label>
              Recipient Email
              <input
                type="email"
                value={backupEmail}
                onChange={(e) => onBackupEmailChange(e.target.value)}
                placeholder="you@example.com"
                spellCheck={false}
              />
            </label>
          </div>
          <div className="data-actions">
            <button className="backup-btn" onClick={doEmailBackup} disabled={emailBusy}>
              {emailBusy ? 'Preparing…' : 'Email Backup'}
            </button>
          </div>
        </div>

        <div className="settings-section">
          <h2>Archive &amp; Clear Old Bills</h2>
          <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
            All sales are kept in a single on-device store that is rewritten on every bill, so a
            very large history slows down billing and reports and can fill device storage. Move
            older bills into a downloaded backup file and clear them from this device to stay fast.
            The file can be re-imported any time to bring those bills back into reports.
          </p>
          <div className="setting-group">
            <label>
              Archive bills older than
              <select value={archiveMonths} onChange={(e) => setArchiveMonths(e.target.value)}>
                <option value="3">3 months</option>
                <option value="6">6 months</option>
                <option value="12">1 year</option>
                <option value="24">2 years</option>
                <option value="custom">A specific date…</option>
              </select>
            </label>
            {archiveMonths === 'custom' && (
              <label>
                Archive bills before
                <input
                  type="date"
                  value={archiveDate}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setArchiveDate(e.target.value)}
                />
              </label>
            )}
          </div>
          {archivePreview && (
            <p style={{ color: 'var(--color-muted)', fontSize: 13 }}>
              {archivePreview.archiveCount > 0 ? (
                <>
                  <strong>{archivePreview.archiveCount}</strong> of {archivePreview.totalCount} bill(s)
                  {archivePreview.from && (
                    <>
                      {' '}
                      ({new Date(archivePreview.from).toLocaleDateString()} –{' '}
                      {new Date(archivePreview.to as string).toLocaleDateString()})
                    </>
                  )}{' '}
                  will be archived, freeing ≈{Math.max(1, Math.round(archivePreview.approxBytes / 1024))} KB.{' '}
                  <strong>{archivePreview.keepCount}</strong> recent bill(s) stay on device.
                </>
              ) : (
                <>No bills are older than the chosen cutoff — nothing to archive.</>
              )}
              {archivePreview.undatedCount > 0 && (
                <> {archivePreview.undatedCount} undated bill(s) are always kept.</>
              )}
            </p>
          )}
          <div className="data-actions">
            <button
              className="backup-btn"
              onClick={doArchive}
              disabled={archiveBusy || !archivePreview || archivePreview.archiveCount === 0}
            >
              {archiveBusy ? 'Archiving…' : 'Archive & Clear'}
            </button>
            <label className="restore-btn" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              Import Archived Bills
              <input type="file" accept="application/json" onChange={importArchive} style={{ display: 'none' }} />
            </label>
          </div>
        </div>

        <div className="settings-section">
          <h2>Data Management</h2>
          <div className="data-actions">
            <button className="backup-btn" onClick={exportData}>
              Export Data
            </button>
            <label className="restore-btn" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              Import Data
              <input type="file" accept="application/json" onChange={importData} style={{ display: 'none' }} />
            </label>
            <button className="clear-data-btn" onClick={clearData}>
              Clear All Data
            </button>
            <button className="restore-btn" onClick={loadDemoData}>
              Load Demo Data
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
